import { z } from 'zod';
import { languageSchema } from '../auth/index';
import { teamMemberSchema } from '../directory/index';

/**
 * Contracten van de module reach: wie heeft een bericht gezien, en wie heeft het bevestigd?
 *
 * Twee eigenschappen verklaren bijna elke vorm hieronder:
 *
 * 1. **Een bevestiging is bewijs.** Ze bevat de versie van de tekst en het tijdstip, en ze is
 *    onveranderlijk (ADR-0042). Dat is de kernwaarde van dit platform voor een productiebedrijf:
 *    aantoonbaar dat een instructie gegeven én gelezen is.
 * 2. **Namen zijn standaard onzichtbaar** (ADR-0014). Het bereik is een aantal en een percentage;
 *    wie precies niets deed, is monitoring van werknemers en vraagt drie voorwaarden tegelijk.
 */

export const reachStatusSchema = z.enum(['not_seen', 'seen', 'acknowledged', 'excluded']);
export type ReachStatus = z.infer<typeof reachStatusSchema>;

/** Waar een namenlijst of een herinnering over gaat. */
export const reachTargetSchema = z.enum(['not_seen', 'not_acknowledged', 'acknowledged']);
export type ReachTarget = z.infer<typeof reachTargetSchema>;

// --- Wat de medewerker ziet ------------------------------------------------------------

/**
 * De eigen stand bij een bericht.
 *
 * Bestaat zodat de app kan weten of de knop "Gelezen en begrepen" nog nodig is, en zodat de
 * medewerker zíjn eigen bevestiging terugziet met tijdstip — hetzelfde bewijs dat zijn werkgever
 * heeft (acceptatiecriterium 6).
 */
export const myReachStatusSchema = z.object({
  postId: z.guid(),
  requiresAcknowledgement: z.boolean(),
  seenAt: z.string().datetime().nullable(),
  acknowledgedAt: z.string().datetime().nullable(),
  /** De versie die je bevestigde. Wijzigt de tekst daarna, dan is dat te zien. */
  acknowledgedVersion: z.number().int().nullable(),
  /**
   * Wanneer de server deze bevestiging ontving (Stap 12).
   *
   * Staat naast `acknowledgedAt`, dat het tijdstip van de medewerker is als hij offline bevestigde.
   * Gelijk aan elkaar bij een bevestiging die meteen aankwam.
   */
  acknowledgedReceivedAt: z.string().datetime().nullable().default(null),
});
export type MyReachStatus = z.infer<typeof myReachStatusSchema>;

/**
 * De eigen standen van meerdere berichten in één keer.
 *
 * Voor de feed: twintig kaarten die elk hun eigen stand opvragen, zijn twintig verzoeken op een
 * telefoon met een trage verbinding.
 */
export const myReachStatusListSchema = z.object({ items: z.array(myReachStatusSchema) });

/**
 * De aantallen die op de kaart van de medewerker staan (Stap 12b, ADR-0049).
 *
 * Bewust een apart contract naast `reachSummarySchema`: dat is het rapport voor een leidinggevende
 * en zit achter `reach.report.view`. Dit is een teller op een berichtkaart, voor iedereen die het
 * bericht mag lezen. Dezelfde gegevens, een andere vraag — en daarom een ander, veel smaller
 * antwoord: getallen, nooit namen, en geen uitsplitsing per groep of rol.
 */
export const feedCountsSchema = z.object({
  postId: z.guid(),
  /**
   * Waar bij een doelgroep kleiner dan vijf alle aantallen wegvallen (ADR-0041).
   *
   * Is dit `true`, dan zijn `viewCount`, `acknowledgedCount` en `recipientCount` alle drie `null`
   * — niet `0`. De client toont dan geen tellerrij: geen getal, geen nul, geen uitleg, want die
   * zouden alle drie meer verklappen dan het getal zelf.
   */
  suppressed: z.boolean(),
  /** Hoeveel mensen het bericht openden, uniek per persoon (ADR-0040). */
  viewCount: z.number().int().nullable(),
  /** Alleen gevuld bij een bericht met bevestigingsplicht; anders `null`. */
  acknowledgedCount: z.number().int().nullable(),
  /** De noemer bij `acknowledgedCount`: hoeveel mensen het bericht hoorden te krijgen. */
  recipientCount: z.number().int().nullable(),
});
export type FeedCounts = z.infer<typeof feedCountsSchema>;

export const feedCountsListSchema = z.object({ items: z.array(feedCountsSchema) });
export type FeedCountsList = z.infer<typeof feedCountsListSchema>;
export type MyReachStatusList = z.infer<typeof myReachStatusListSchema>;

/**
 * Het tijdstip volgens de telefoon van de medewerker (Stap 12).
 *
 * Een bevestiging die in een koelcel gegeven is, komt uren later aan. Zonder dit veld zou in het
 * bewijs staan dat hij om 14:20 bevestigde terwijl hij het om 06:55 deed (ADR-0047).
 *
 * Maximaal zeven dagen oud: de wachtrij op het toestel probeert het zo lang, en daarna is het geen
 * vertraagde bevestiging meer maar een oud bericht uit een vergeten app. Ouder dan dat geeft `422`.
 */
export const clientOccurredAtSchema = z.string().datetime();

/**
 * Het lichaam van `POST /feed/:postId/seen`.
 *
 * `.default({})` en niet alleen een optioneel veld: een verzoek **zonder** lichaam moet blijven
 * werken. Dat is het gewone geval — een medewerker die online een bericht opent, stuurt geen
 * tijdstip mee, en een `400` daarop zou betekenen dat "gezien" stil stopt te werken zodra dit veld
 * bestaat.
 */
export const seenRequestSchema = z
  .object({
    clientOccurredAt: clientOccurredAtSchema.optional(),
  })
  .default({});
export type SeenRequest = z.infer<typeof seenRequestSchema>;

export const acknowledgeRequestSchema = z.object({
  /** De versie die op het scherm stond. Bevestigen kan alleen voor de actuele versie. */
  postVersion: z.number().int().min(1),
  /** In welke taal de lezer de tekst zag; vanaf Stap 11 kan dat een vertaling zijn. */
  languageShown: z.string().min(2).max(5).default('nl'),
  /** Wanneer hij op de knop drukte; leeg als dat nu was (Stap 12). */
  clientOccurredAt: clientOccurredAtSchema.optional(),
});
export type AcknowledgeRequest = z.infer<typeof acknowledgeRequestSchema>;

// --- Wat communicatie ziet -------------------------------------------------------------

/**
 * Eén regel in de uitsplitsing: een groep of een rol.
 *
 * Groepen met minder dan vijf mensen worden samengevoegd tot één regel "overige"
 * (ADR-0041): in een ploeg van drie is "1 van de 3 bevestigd" een naam.
 */
export const reachBreakdownRowSchema = z.object({
  /** Het id van de groep of rol; leeg bij de samengevoegde regel. */
  id: z.guid().nullable(),
  /** Bij een rol: de Nederlandse naam. */
  label: z.string(),
  /**
   * Bij een rol: de naam per taal, het scherm toont die van de lezer. `null` bij een groep (die heeft
   * één naam) en bij de samengevoegde regel.
   */
  labels: z.partialRecord(languageSchema, z.string()).nullable().default(null),
  expected: z.number().int(),
  seen: z.number().int(),
  acknowledged: z.number().int(),
  /** Deze regel is een samenvoeging van groepen die te klein zijn om te tonen. */
  merged: z.boolean(),
});
export type ReachBreakdownRow = z.infer<typeof reachBreakdownRowSchema>;

export const reachSummarySchema = z.object({
  postId: z.guid(),
  title: z.string(),
  requiresAcknowledgement: z.boolean(),
  /**
   * Hoeveel mensen het bericht kregen én nog meetellen. Wie uit dienst is, valt eruit — zijn
   * eerdere bevestiging blijft wel in het bewijs staan (ADR-0042).
   */
  expected: z.number().int(),
  excluded: z.number().int(),
  seen: z.number().int(),
  acknowledged: z.number().int(),
  byGroup: z.array(reachBreakdownRowSchema),
  byRole: z.array(reachBreakdownRowSchema),
  /** Mogen er namen bij? Zo niet, dan staat hier waarom (ADR-0014). */
  namesVisible: z.boolean(),
  namesWithheldReason: z
    .enum(['no_acknowledgement_required', 'no_permission', 'disabled_for_tenant'])
    .nullable(),
  /** Wanneer er voor het laatst een herinnering uitging; er mag er één per twaalf uur. */
  lastReminderAt: z.string().datetime().nullable(),
  /** Hoeveel herinneringen er uitgingen, en hoeveel daarvan automatisch (Stap 16). */
  remindersSent: z.number().int().default(0),
  automaticReminders: z.number().int().default(0),
  publishedAt: z.string().datetime().nullable(),
});
export type ReachSummary = z.infer<typeof reachSummarySchema>;

/** Percentages voor de berichtenlijst; geen namen, geen uitsplitsing. */
export const reachListItemSchema = z.object({
  postId: z.guid(),
  expected: z.number().int(),
  seen: z.number().int(),
  acknowledged: z.number().int(),
  requiresAcknowledgement: z.boolean(),
});
export type ReachListItem = z.infer<typeof reachListItemSchema>;

export const reachListSchema = z.object({ items: z.array(reachListItemSchema) });
export type ReachList = z.infer<typeof reachListSchema>;

/**
 * Eén persoon in een namenlijst.
 *
 * Alleen te krijgen als alle drie de voorwaarden van ADR-0014 gelden. De groepen staan erbij
 * omdat een lijst van tweehonderd namen zonder ploeg niets zegt tegen wie ermee moet werken.
 */
export const reachPersonSchema = z.object({
  personId: z.guid(),
  displayName: z.string(),
  employeeNumber: z.string().nullable(),
  groups: z.array(z.string()),
  seenAt: z.string().datetime().nullable(),
  acknowledgedAt: z.string().datetime().nullable(),
  acknowledgedVersion: z.number().int().nullable(),
  /**
   * De taal waarin deze persoon de tekst zag toen hij bevestigde (Stap 11).
   *
   * Hoort bij het bewijs en niet bij de statistiek: "hij heeft bevestigd" zegt weinig zonder
   * "hij heeft deze versie in deze taal gezien". Leeg zolang hij niet bevestigd heeft.
   */
  acknowledgedLanguage: z.string().nullable().default(null),
  excludedAt: z.string().datetime().nullable(),
  /**
   * Hoe de bevestiging binnenkwam (Stap 42a): in de app, op een kiosk, of op papier - dan met wie
   * ze registreerde en de dag van de handtekening. Metagegevens; de keten zelf is dezelfde.
   */
  acknowledgedVia: z.enum(['app', 'kiosk', 'paper']).nullable().default(null),
  paperRecordedByName: z.string().nullable().default(null),
  paperSignedOn: z.string().date().nullable().default(null),
});
export type ReachPerson = z.infer<typeof reachPersonSchema>;

export const reachPeoplePageSchema = z.object({
  items: z.array(reachPersonSchema),
  nextCursor: z.string().nullable(),
});
export type ReachPeoplePage = z.infer<typeof reachPeoplePageSchema>;

export const reachPeopleQuerySchema = z.object({
  status: reachTargetSchema.default('not_acknowledged'),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export type ReachPeopleQuery = z.infer<typeof reachPeopleQuerySchema>;

export const reminderRequestSchema = z.object({
  target: z.enum(['not_seen', 'not_acknowledged']).default('not_acknowledged'),
});
export type ReminderRequest = z.infer<typeof reminderRequestSchema>;

export const reminderResultSchema = z.object({
  /** Hoeveel mensen er een herinnering krijgen. Nul is een geldig antwoord. */
  recipients: z.number().int(),
  target: z.enum(['not_seen', 'not_acknowledged']),
});
export type ReminderResult = z.infer<typeof reminderResultSchema>;

// --- Mijn team (Stap 17, TEAM-1) -----------------------------------------------------

/**
 * Waarom er geen namen bij staan, in de woorden van het teamscherm (spec §2, frame 2b).
 *
 * Drie redenen en niet één, omdat het scherm drie verschillende dingen uitlegt: er is geen
 * leesbevestiging gevraagd, jij hebt het recht niet, of dit bedrijf zet namen uit. Eén algemene
 * zin zou de teamleider bij de verkeerde persoon doen aankloppen.
 */
export const teamNamesWithheldReasonSchema = z.enum(['no_ack', 'no_right', 'tenant_off']);
export type TeamNamesWithheldReason = z.infer<typeof teamNamesWithheldReasonSchema>;

/** Eén verplicht bericht dat naar het team ging, met de cijfers van dat team erbij. */
export const teamPostSchema = z.object({
  postId: z.guid(),
  title: z.string(),
  publishedAt: z.string().datetime().nullable(),
  deadlineAt: z.string().datetime().nullable(),
  /** Hoeveel teamleden het bericht kregen. Gedeactiveerde leden tellen niet mee. */
  expected: z.number().int(),
  seen: z.number().int(),
  acknowledged: z.number().int(),
  /**
   * Geen uitsplitsing tonen: het team is kleiner dan vijf en er is geen leesbevestiging gevraagd
   * (ADR-0041, spec §4). Het scherm toont dan "Bericht verstuurd naar je team" zonder balk en
   * zonder cijfers - en de cijfers hierboven staan op nul.
   */
  countsSuppressed: z.boolean(),
});
export type TeamPost = z.infer<typeof teamPostSchema>;

export const teamPostListSchema = z.object({ items: z.array(teamPostSchema) });
export type TeamPostList = z.infer<typeof teamPostListSchema>;

/** Eén teamlid bij één bericht, met de stand die de teamleider mag zien. */
export const teamPostPersonSchema = z.object({
  personId: z.guid(),
  displayName: z.string(),
  photoFileId: z.guid().nullable(),
  seenAt: z.string().datetime().nullable(),
  acknowledgedAt: z.string().datetime().nullable(),
  /** Wanneer deze persoon voor dit bericht herinnerd werd; voor "Herinnerd om 08:12". */
  remindedAt: z.string().datetime().nullable(),
});
export type TeamPostPerson = z.infer<typeof teamPostPersonSchema>;

/**
 * Eén bericht in het team, met namen als ADR-0014 a + b + c gelden.
 *
 * `people` is `null` en niet een lege lijst wanneer de namen niet mogen: een lege lijst zou
 * lezen als "iedereen heeft bevestigd", en dat is het ergste antwoord dat dit scherm kan geven.
 */
export const teamPostDetailSchema = teamPostSchema.extend({
  people: z
    .object({
      notAcknowledged: z.array(teamPostPersonSchema),
      acknowledged: z.array(teamPostPersonSchema),
    })
    .nullable(),
  namesWithheldReason: teamNamesWithheldReasonSchema.nullable(),
});
export type TeamPostDetail = z.infer<typeof teamPostDetailSchema>;

/**
 * Herinneren: één persoon of iedereen die nog niet bevestigde.
 *
 * `all` bestaat apart van `personId` omdat "herinner iedereen" ook werkt zonder namen (spec §1):
 * wie geen namen mag zien, mag zijn ploeg wél aansporen.
 */
export const teamReminderRequestSchema = z
  .object({
    personId: z.guid().optional(),
    all: z.boolean().optional(),
  })
  .refine((body) => Boolean(body.personId) !== Boolean(body.all), {
    message: 'Kies één persoon of iedereen, niet beide en niet geen van beide.',
  });
export type TeamReminderRequest = z.infer<typeof teamReminderRequestSchema>;

export const teamReminderResultSchema = z.object({
  recipients: z.number().int(),
  /** Het tijdstip dat het scherm toont: "Herinnerd om 08:12". */
  remindedAt: z.string().datetime(),
});
export type TeamReminderResult = z.infer<typeof teamReminderResultSchema>;

/**
 * Hoeveel verplichte berichten elk teamlid nog moet bevestigen; voor de badge "2 open".
 *
 * Een aantal en geen titels: de titels staan achter de namen-poort, het aantal niet. Dat
 * onderscheid is ADR-0049 - aantallen mogen altijd.
 */
export const teamOpenCountsSchema = z.object({
  counts: z.record(z.guid(), z.number().int()),
});
export type TeamOpenCounts = z.infer<typeof teamOpenCountsSchema>;

/**
 * Eén teamlid met de blokken die alleen zijn leidinggevende ziet (spec §2, frame 3).
 *
 * `openPosts` is `null` wanneer de namen-poort dicht is: welke berichten iemand nog niet
 * bevestigde, is een naam met een lijst eraan vast en valt onder ADR-0014. Het aantal mag wel -
 * dat staat in `member.openMandatory`.
 */
export const teamMemberDetailSchema = z.object({
  member: teamMemberSchema,
  openPosts: z
    .array(
      z.object({
        postId: z.guid(),
        title: z.string(),
        deadlineAt: z.string().datetime().nullable(),
      }),
    )
    .nullable(),
  namesWithheldReason: teamNamesWithheldReasonSchema.nullable(),
});
export type TeamMemberDetail = z.infer<typeof teamMemberDetailSchema>;

// --- Automatische herinneringen (Stap 16) -------------------------------------------------

/**
 * Het herinneringsschema van een bericht met bevestigingsplicht.
 *
 * `offsetsHours`: na hoeveel uur een herinnering vertrekt naar wie nog niet bevestigde (standaard
 * 24). `escalateAfterHours`: de laatste stap, de melding aan de leidinggevende (TEAM-1); `null` is
 * de standaard van het bedrijf.
 */
export const reminderPolicySchema = z.object({
  offsetsHours: z.array(z.number().int().min(1).max(720)).max(5),
  escalateAfterHours: z.number().int().min(1).max(720).nullable(),
});
export type ReminderPolicy = z.infer<typeof reminderPolicySchema>;

export const reminderPolicyViewSchema = reminderPolicySchema.extend({
  /** Of dit de standaard is of door de schrijver gekozen. */
  isDefault: z.boolean(),
  /** De termijn van het bedrijf, voor de tekst "standaard (24 u)". */
  tenantEscalationHours: z.number().int(),
});
export type ReminderPolicyView = z.infer<typeof reminderPolicyViewSchema>;

// --- Afvinklijst op papier (Stap 42a) -------------------------------------------------------

/** Papieren bevestigingen registreren: wie tekende, en op welke dag. */
export const paperAcknowledgementRequestSchema = z.object({
  personIds: z.array(z.guid()).min(1).max(500),
  signedOn: z.string().date(),
});
export type PaperAcknowledgementRequest = z.infer<typeof paperAcknowledgementRequestSchema>;

export const paperAcknowledgementResultSchema = z.object({
  /** Nieuw geregistreerd. */
  recorded: z.number().int(),
  /** Had al bevestigd (in de app, op een kiosk of eerder op papier); de eerste blijft staan. */
  alreadyAcknowledged: z.number().int(),
});
export type PaperAcknowledgementResult = z.infer<typeof paperAcknowledgementResultSchema>;
